import { CurrencyOptionResource } from "@dgs/core";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { EventBudgetAnalyticsCostResource, EventBudgetAnalyticsCostsByCategoryResource } from "../analyticsTypes";

export interface BudgetAnalyticsGridRowData {
	name: string;
	cells: EventBudgetAnalyticsCostResource[];
}

export interface BudgetAnalyticsGridData {
	columns: CurrencyOptionResource[];
	rows: BudgetAnalyticsGridRowData[];
}

function isUnique(currency: CurrencyOptionResource, index: number, currencies: CurrencyOptionResource[]) {
	return currencies.findIndex((c) => c.isoCode === currency.isoCode) === index;
}

export function useBudgetAnalyticsGridData(
	acceptedUserRequestCosts: EventBudgetAnalyticsCostResource[],
	costsByCategory: EventBudgetAnalyticsCostsByCategoryResource[],
	contactCosts: EventBudgetAnalyticsCostResource[],
): BudgetAnalyticsGridData {
	const { t } = useTranslation();

	const columns = useMemo(
		() =>
			contactCosts
				.map((cc) => cc.currency)
				.concat(acceptedUserRequestCosts.map((rc) => rc.currency))
				.concat(costsByCategory.flatMap((cbc) => cbc.costs.map((c) => c.currency)))
				.filter(isUnique),
		[acceptedUserRequestCosts, contactCosts, costsByCategory],
	);

	const rows = useMemo(() => {
		const userRequestRow: BudgetAnalyticsGridRowData = {
			name: t("Approved participation requests"),
			cells: columns.map((currency) => ({
				currency,
				costs: acceptedUserRequestCosts.find((cost) => cost.currency.isoCode === currency.isoCode)?.costs || 0,
			})),
		};

		const contactCostsRow: BudgetAnalyticsGridRowData = {
			name: t("Costs for contacts"),
			cells: columns.map((currency) => ({
				currency,
				costs: contactCosts.find((cost) => cost.currency.isoCode === currency.isoCode)?.costs || 0,
			})),
		};

		const costByCategoryRows: BudgetAnalyticsGridRowData[] = costsByCategory.map((category) => ({
			name: category.costCategory.name,
			cells: columns.map((currency) => ({
				currency,
				costs: category.costs.find((cost) => cost.currency.isoCode === currency.isoCode)?.costs || 0,
			})),
		}));

		return [userRequestRow, contactCostsRow, ...costByCategoryRows];
	}, [acceptedUserRequestCosts, columns, contactCosts, costsByCategory, t]);

	return {
		columns,
		rows,
	};
}
